import { createSelector, createSlice } from '@reduxjs/toolkit';
import { persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import { fetchScopedWarehouses } from './warehouseContextThunks';

/*
 * The warehouse being worked in.
 *
 * Inside the module the warehouse is not a filter of each screen: it is chosen
 * once, above, and every screen inherits it. Receiving, storing, picking and
 * counting happen in a physical place, and asking "where?" on every screen is
 * asking the same thing five times.
 */

const initialState = {
  warehouses: [],
  loading: false,
  /** Set after the first answer, so the screens wait for it only once. */
  loaded: false,
  error: null,
  /**
   * `null` means "todas mis bodegas": a fair answer for looking (stock,
   * orders, products) and an invalid one for operating (receiving, putaway,
   * locations, supplies).
   */
  selectedId: null,
};

/**
 * Which warehouse stays selected once the reachable ones are known.
 *
 * A remembered warehouse that is no longer in reach is dropped rather than
 * sent to the server, and a single warehouse is not a choice.
 */
const resolveSelection = (warehouses, selectedId) => {
  if (warehouses.length === 1) return warehouses[0].id;
  return warehouses.some((warehouse) => warehouse.id === selectedId) ? selectedId : null;
};

const warehouseContextSlice = createSlice({
  name: 'warehouseContext',
  initialState,
  reducers: {
    /** @param {{payload: number|null}} action - `null` for every warehouse. */
    selectWarehouse: (state, action) => {
      state.selectedId = action.payload ?? null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchScopedWarehouses.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchScopedWarehouses.fulfilled, (state, action) => {
        state.loading = false;
        state.loaded = true;
        state.warehouses = action.payload;
        state.selectedId = resolveSelection(action.payload, state.selectedId);
      })
      .addCase(fetchScopedWarehouses.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { selectWarehouse } = warehouseContextSlice.actions;

export const selectWarehouses = (state) => state.warehouseContext.warehouses;

/** @returns {number|null} */
export const selectCurrentWarehouseId = (state) => state.warehouseContext.selectedId;

/** @returns {object|null} The warehouse itself, or `null` for all of them. */
export const selectCurrentWarehouse = createSelector(
  [selectWarehouses, selectCurrentWarehouseId],
  (warehouses, selectedId) =>
    warehouses.find((warehouse) => warehouse.id === selectedId) || null
);

/**
 * Whether work can happen here: operating needs one concrete place, and
 * somebody with an active contract inside it.
 *
 * @returns {boolean}
 */
export const selectCanOperateWarehouse = (state) =>
  Boolean(selectCurrentWarehouse(state)?.clients?.length);

/**
 * Whether any warehouse in reach is this account's own to run. A seller that
 * holds one puts away and keeps supplies there; one that only keeps goods in
 * somebody else's centre does not, and its menu says so.
 *
 * @returns {boolean}
 */
export const selectHoldsAnyWarehouse = (state) =>
  selectWarehouses(state).some((warehouse) => Boolean(warehouse.can_manage));

/**
 * The ids of the warehouses this account runs. An order in one of them can be
 * prepared by whoever holds it, whatever the role says; an order in somebody
 * else's centre cannot.
 *
 * @returns {Array<number>}
 */
export const selectHeldWarehouseIds = createSelector([selectWarehouses], (warehouses) =>
  warehouses.filter((warehouse) => Boolean(warehouse.can_manage)).map((warehouse) => warehouse.id)
);

/**
 * The ids of the warehouses where this account may reserve orders. Whoever
 * prepares reserves; a seller in its operator's centre does too when the
 * contract says so, and nothing else. The server works it out (`can_reserve`).
 *
 * @returns {Array<number>}
 */
export const selectReservableWarehouseIds = createSelector([selectWarehouses], (warehouses) =>
  warehouses.filter((warehouse) => Boolean(warehouse.can_reserve)).map((warehouse) => warehouse.id)
);

/** The companies holding a contract in these warehouses, once each, in order. */
const clientOptionsOf = (warehouses) => {
  const byId = new Map();
  warehouses.forEach((warehouse) => {
    (warehouse.clients || []).forEach((contract) => {
      if (!byId.has(contract.company)) byId.set(contract.company, contract.company_name);
    });
  });

  return [...byId.entries()]
    .map(([value, label]) => ({ value, label: label || `Empresa ${value}` }))
    .sort((a, b) => a.label.localeCompare(b.label, 'es'));
};

/**
 * The clients to filter by, as select options: those of the warehouse in use,
 * or of every warehouse in reach when none is picked.
 *
 * Deduplicated because one company can hold a contract in two centres. A
 * seller's list has only itself — the server trims it, since who it shares the
 * centre with is none of its business.
 *
 * @returns {Array<{value: number, label: string}>}
 */
export const selectWarehouseClientOptions = createSelector(
  [selectWarehouses, selectCurrentWarehouse],
  (warehouses, current) => clientOptionsOf(current ? [current] : warehouses)
);

/**
 * Every client of every warehouse in reach, whichever warehouse is in use.
 *
 * For what belongs to the company and not to a place: its catalog is the same
 * in the two centres it stores in, so picking a warehouse must not hide it.
 *
 * @returns {Array<{value: number, label: string}>}
 */
export const selectAllClientOptions = createSelector([selectWarehouses], (warehouses) =>
  clientOptionsOf(warehouses)
);

// Only the choice survives a reload. The list is asked again every time: what
// is in reach is the server's call, and a remembered list would be a stale one.
const persistConfig = {
  key: 'warehouseContext',
  storage,
  whitelist: ['selectedId'],
};

export default persistReducer(persistConfig, warehouseContextSlice.reducer);
